import Image from 'next/legacy/image';
import { format } from 'date-fns';

import arrowLeft from '../../../public/images/arrow-left.svg';
import { BuildingStats } from '../../../apolloClient/types/Building';

import { KeyValueList } from '../../KeyValueList';
import Link from 'components/CustomLink';

interface SalesHistory {
  [key: string]: number;
}
interface SalesHistoryData {
  [key: string]: number;
}

type SalesHistoryProps = {
  href: string;
  showButton?: string;
  buildingStats: BuildingStats;
};

const parseSalesHistory = (
  saleHistory?: string | SalesHistoryData
): SalesHistoryData => {
  if (!saleHistory) return {};

  if (typeof saleHistory === 'string') {
    try {
      return JSON.parse(saleHistory) as SalesHistoryData;
    } catch (error) {
      console.error('Error parsing JSON in SalesHistory:', error);
      return {};
    }
  }

  return typeof saleHistory === 'object' ? saleHistory : {};
};

const generateSalesHistoryList = (salesHistory: SalesHistoryData) => {
  return Array.from({ length: 6 }, (_, index) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const indexMonthAgoDate = new Date(currentYear, currentMonth - index);
    const formattedKey = format(indexMonthAgoDate, 'MMMM yyyy');
    const formattedValue = format(indexMonthAgoDate, 'yyyy-MM');

    return {
      key: formattedKey,
      value: salesHistory[formattedValue] || 0,
    };
  });
};

const SalesHistory: React.FC<SalesHistoryProps> = ({
  href,
  showButton,
  buildingStats,
}) => {
  const salesHistory = parseSalesHistory(buildingStats.saleHistory);
  const salesHistoryList = generateSalesHistoryList(salesHistory);

  const showSeeRecentButton = salesHistoryList.some(
    (entry, index) => index < 5 && entry.value > 0
  );

  return (
    <>
      <div className="block xl:hidden w-fill mt-12 h-0.5 bg-beige" />
      <div className="flex flex-col pt-[26px] md:w-2/3 xl:w-1/3 xl:border-l xl:border-beige-dark">
        <div className="flex flex-col xl:mt-[88px] xl:ml-8">
          <h2 className="text-18-28-0.3 md:text-21-28-0.3 uppercase font-sohneKraftig">
            Sales history
          </h2>
          <p className="text-14-26-0.3 md:text-16-26-0.3">
            Number of sold condos by month
          </p>
        </div>

        <div className="mt-[15px] md:mt-6 xl:ml-8 lg:mt-[17px]">
          <KeyValueList
            rowsClassName="lg:text-16-26-0.3"
            colRightClassName="font-sohneKraftig flex justify-end py-[6px] md:py-[4px] text-14-22-0.3 md:text-21-28-0.3"
            colLeftClassName="py-[6px] md:py-[4px] text-14-22-0.3 md:text-16-26-0.3"
            rows={salesHistoryList}
          />
        </div>

        {showSeeRecentButton && (
          <div className={`${showButton} xl:pl-8 xl:pt-4`}>
            <Link
              passHref
              href={href}
              className="flex items-center uppercase border-b-2 cursor-pointer border-gold-dark md:mb-10 xl:mb-6 w-max"
            >
              <div className="w-5 h-4">
                <Image src={arrowLeft} alt="See recent sales" width={15} />
              </div>
              <span className="whitespace-nowrap">See recent sales</span>
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default SalesHistory;
